import { Controller } from "stimulus"
import DOMPurify from "dompurify"
import { marked } from "marked"

export default class extends Controller {
  static targets = ["input", "messages", "status", "submit", "window", "reset"]
  static values = { url: String }

  connect() {
    this.conversationId = null
    this.loaded = false
    this.fresh = false
    this.pending = false
  }

  disconnect() {
    if (this.request) this.request.abort()
  }

  reset() {
    if (this.pending) return
    this.conversationId = null
    this.loaded = true
    this.fresh = true
    if (this.request) this.request.abort()
    this.request = null
    this.messagesTarget.replaceChildren()
    this.inputTarget.value = ""
    this.resizeInput()
    this.statusTarget.textContent = ""
    this.setPending(false)
  }

  async open(event) {
    this.trigger = event && event.currentTarget
    if (this.trigger) this.trigger.setAttribute("aria-expanded", "true")
    this.windowTarget.hidden = false
    if (!this.pending && !this.fresh) await this.loadConversation()
    if (!this.pending) this.inputTarget.focus()
    this.scrollToBottom()
  }

  close() {
    if (this.windowTarget.hidden) return
    this.windowTarget.hidden = true
    if (this.trigger) this.trigger.setAttribute("aria-expanded", "false")
    if (this.trigger && this.trigger.isConnected) this.trigger.focus()
  }

  async loadConversation() {
    const request = new AbortController()
    this.request = request
    this.setPending(true)
    this.statusTarget.textContent = "Chargement de votre discussion…"
    try {
      const url = new URL(this.urlValue, window.location.origin)
      if (this.conversationId) url.searchParams.set("conversation_id", this.conversationId)
      const response = await fetch(url.href, { credentials: "same-origin", headers: { Accept: "application/json" }, signal: request.signal })
      if (!response.ok || response.redirected) throw new Error("Votre discussion n’a pas pu être chargée. Fermez puis rouvrez le chat pour réessayer.")
      const data = await response.json()
      if (this.request !== request) return
      this.conversationId = data.conversation_id
      this.messagesTarget.replaceChildren()
      data.messages.forEach(item => {
        const message = this.addMessage(item.content, item.role)
        this.addSources(message, item.sources || [])
      })
      this.loaded = true
      this.statusTarget.textContent = ""
      this.scrollToBottom()
    } catch (error) {
      if (error.name !== "AbortError") this.statusTarget.textContent = error.message
    } finally {
      if (this.request === request) {
        this.request = null
        this.setPending(false)
      }
    }
  }

  keydown(event) {
    if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
      event.preventDefault()
      this.send(event)
    }
  }

  resizeInput() {
    this.inputTarget.style.height = "auto"
    this.inputTarget.style.height = `${Math.min(120, Math.max(40, this.inputTarget.scrollHeight))}px`
  }

  async send(event) {
    event.preventDefault()
    if (this.pending) return
    if (!this.loaded) {
      await this.loadConversation()
      if (!this.loaded) return
    }
    const question = this.inputTarget.value.trim()
    if (!question) return

    const request = new AbortController()
    this.request = request
    this.setPending(true)
    this.statusTarget.textContent = "Recherche dans vos ressources"
    this.statusTarget.classList.add("is-loading")
    const userMessage = this.addMessage(question, "user")
    let assistantMessage = null
    let partialAnswer = ""
    this.inputTarget.value = ""
    this.resizeInput()
    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ resource_question: question, conversation_id: this.conversationId }),
        signal: request.signal
      })
      if (response.redirected || response.status === 401 || response.status === 403) {
        throw new Error("Reconnectez-vous avec votre compte de patient partenaire pour continuer.")
      }
      const data = await this.readReply(response, delta => {
        if (this.request !== request) return
        partialAnswer += delta
        if (!assistantMessage) assistantMessage = this.addMessage("", "assistant")
        this.renderAssistant(assistantMessage, partialAnswer)
        this.statusTarget.textContent = ""
        this.statusTarget.classList.remove("is-loading")
        this.scrollToBottom()
      }, start => {
        this.conversationId = start.conversation_id
        this.fresh = false
      })
      if (data.conversation_id) {
        this.conversationId = data.conversation_id
        this.fresh = false
      }
      if (!response.ok) throw new Error(data.error || "La réponse n’a pas pu être obtenue.")
      if (this.request !== request) return

      const message = assistantMessage || this.addMessage("", "assistant")
      this.renderAssistant(message, data.answer)
      this.addSources(message, data.sources || [])
      this.inputTarget.value = ""
      this.resizeInput()
      this.statusTarget.textContent = data.sources && data.sources.length
        ? "Réponse reçue. Les sources sont disponibles sous la réponse."
        : "Réponse reçue."
      this.scrollToBottom()
    } catch (error) {
      if (error.name === "AbortError" || this.request !== request) return
      userMessage.remove()
      if (assistantMessage) assistantMessage.remove()
      this.inputTarget.value = question
      this.resizeInput()
      this.statusTarget.textContent = ["TypeError", "SyntaxError"].includes(error.name)
        ? "La réponse n’a pas pu être reçue. Vérifiez votre connexion et réessayez."
        : (error.message || "La connexion a échoué. Réessayez.")
    } finally {
      if (this.request === request) {
        this.request = null
        this.setPending(false)
        if (!this.windowTarget.hidden) this.inputTarget.focus()
      }
    }
  }

  addMessage(text, role) {
    const element = document.createElement("div")
    element.className = `resource-chat__message resource-chat__message--${role}`
    if (role === "user" || role === "error") {
      element.textContent = text
    } else {
      this.renderAssistant(element, text)
    }
    this.messagesTarget.appendChild(element)
    this.scrollToBottom()
    return element
  }

  async readReply(response, onDelta, onStart) {
    if (!response.headers?.get("Content-Type")?.includes("text/event-stream")) return response.json()
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ""
    let result = null
    const consume = frame => {
      const lines = frame.split(/\r?\n/)
      const event = lines.find(line => line.startsWith("event:"))?.slice(6).trim()
      const data = lines.filter(line => line.startsWith("data:")).map(line => line.slice(5).trimStart()).join("\n")
      if (!data) return
      const payload = JSON.parse(data)
      if (event === "start") onStart(payload)
      if (event === "delta") onDelta(payload.content || "")
      if (event === "error") throw new Error(payload.error || "La réponse a été interrompue. Rouvrez le chat pour retrouver votre discussion.")
      if (event === "done") result = payload
    }
    try {
      while (true) {
        const { done, value } = await reader.read()
        buffer += done ? decoder.decode() : decoder.decode(value, { stream: true })
        let boundary
        while ((boundary = /\r?\n\r?\n/.exec(buffer))) {
          consume(buffer.slice(0, boundary.index))
          buffer = buffer.slice(boundary.index + boundary[0].length)
        }
        if (done) break
      }
      if (buffer.trim()) consume(buffer)
      if (!result) throw new Error("La réponse a été interrompue. Rouvrez le chat pour retrouver votre discussion.")
      return result
    } finally {
      reader.releaseLock()
    }
  }

  renderAssistant(element, text) {
    element.innerHTML = DOMPurify.sanitize(marked.parse(text), {
      FORBID_TAGS: ["img", "form", "input", "button", "style"]
    })
    element.querySelectorAll("a").forEach(link => {
      // Document links are built below from server-verified citations.
      link.replaceWith(document.createTextNode(link.textContent))
    })
  }

  addSources(message, sources) {
    if (!sources.length) return
    const container = document.createElement("div")
    container.className = "resource-chat__sources"
    const heading = document.createElement("strong")
    heading.textContent = "Sources"
    container.appendChild(heading)
    sources.forEach((source, index) => {
      const url = new URL(source.url, window.location.origin)
      if (url.origin !== window.location.origin) return
      const link = document.createElement("a")
      link.href = url.href
      link.target = "_blank"
      link.rel = "noopener noreferrer"
      link.textContent = `[${index + 1}] ${source.title}${source.page ? ` — p. ${source.page}` : ""}`
      container.appendChild(link)
    })
    message.appendChild(container)
  }

  setPending(pending) {
    this.pending = pending
    if (!pending) this.statusTarget.classList.remove("is-loading")
    this.submitTarget.disabled = pending
    this.resetTarget.disabled = pending
    this.inputTarget.disabled = pending
    this.messagesTarget.setAttribute("aria-busy", pending ? "true" : "false")
  }

  scrollToBottom() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }
}
